import type {
  IFreeQuestionOption,
  IQuestionOption,
  QuestionOptions,
} from '@duke-hq/svc-api/provider/trpc/routers/tenant/IQuestionOption';
import type { HintType } from '../ReactFlow/nodes/blocknode/QuizHint';

/**
 * `Question.options` and `Question.hint` are untyped Json columns, so what comes
 * back from the API is only known at runtime. These narrow it before it reaches
 * component state, which stops a malformed row rendering as a broken editor.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isQuestionOption(value: unknown): value is IQuestionOption {
  return isRecord(value) && typeof value.id === 'string' && typeof value.content === 'string';
}

function isFreeQuestionOption(value: unknown): value is IFreeQuestionOption {
  return (
    isRecord(value) && typeof value.minLength === 'number' && typeof value.maxLength === 'number'
  );
}

export function asQuestionOptions(value: unknown): QuestionOptions | undefined {
  if (Array.isArray(value)) {
    return value.every(isQuestionOption) ? value : undefined;
  }
  return isFreeQuestionOption(value) ? value : undefined;
}

export function asHint(value: unknown): HintType {
  if (isRecord(value) && typeof value.message === 'string' && typeof value.type === 'string') {
    const { message, type, inactivity_seconds: inactivitySeconds } = value;
    return {
      message,
      type,
      ...(typeof inactivitySeconds === 'number' ? { inactivity_seconds: inactivitySeconds } : {}),
    };
  }
  return undefined;
}
